import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";

const SENTIMENTS = ["Bullish", "Somewhat-Bullish", "Neutral", "Somewhat-Bearish", "Bearish"];

interface NewsItem {
  url?: string;
  banner_image?: string;
  time_published?: string;
  title?: string;
  overall_sentiment_label?: string;
  [key: string]: unknown;
}

interface NewsCardProps {
  news?: NewsItem[];
  limitToShow?: number;
  lastUpdated?: string;
  isLoading?: boolean;
}

const NewsCard = ({ news = [], limitToShow = 10, lastUpdated = undefined, isLoading = false }: NewsCardProps) => {
  const [selectedSent, setSelectedSent] = useState<string | null>(null);
  const { t, i18n } = useTranslation("news", { keyPrefix: "newsCard" });

  const filteredNews = news.filter(({ overall_sentiment_label }) => {
    if (!selectedSent) return true;
    return overall_sentiment_label === selectedSent;
  });

  return (
    <div className="card-modern">
      <div className="card-header-modern">
        <div>
          <div className="card-title">
            <i className="fas fa-newspaper mr-2" style={{ opacity: 0.5 }} />
            {t("title")}
          </div>
          <div className="card-subtitle">
            {lastUpdated ? moment(lastUpdated).locale(i18n.language).format("LLL") : "---"}
          </div>
        </div>

        <div className="card-actions">
          <UncontrolledDropdown size="sm" direction="left">
            <DropdownToggle
              color="default"
              caret={false}
              className="btn-modern btn-secondary btn-sm"
              style={{ padding: "6px 12px", fontSize: "0.75rem" }}
            >
              {t(`sentiments.${selectedSent || "all"}`)}
              <i className="fas fa-chevron-down ml-2" style={{ fontSize: "0.625rem" }} />
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => setSelectedSent(null)}>{t("sentiments.all")}</DropdownItem>
              {SENTIMENTS.map((sent) => (
                <DropdownItem key={sent} onClick={() => setSelectedSent(sent)} active={selectedSent === sent}>
                  {t(`sentiments.${sent}`)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </div>

      <div className="card-body-modern">
        <div className="widget-list">
          {filteredNews.slice(0, limitToShow).map(({ url, banner_image, time_published, title }, index) => (
            <a
              key={url ?? index}
              href={url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="widget-item animate-stagger"
              style={{
                textDecoration: "none",
                color: "inherit",
                animationDelay: `${index * 0.05}s`,
              }}
            >
              {banner_image && (
                <div className="widget-item-image">
                  <img src={banner_image} alt="" />
                </div>
              )}
              <div className="widget-item-content">
                <div className="meta">
                  {time_published ? moment(time_published).locale(i18n?.language).format("LLL") : "---"}
                </div>
                <div className="title">{title}</div>
              </div>
            </a>
          ))}

          {isLoading &&
            Array.from({ length: Math.min(limitToShow, 4) }).map((_, i) => (
              <div key={`news-skeleton-${i}`} className="widget-item" style={{ pointerEvents: "none" }}>
                <div
                  style={{
                    height: 14,
                    width: `${85 - i * 10}%`,
                    borderRadius: 4,
                    background: "#f3f4f6",
                    margin: "6px 0",
                  }}
                />
              </div>
            ))}

          {/* Without the loading guard, "still fetching" and "no news" rendered
              identically, so the empty state flashed on every load. */}
          {!isLoading && filteredNews.length <= 0 && (
            <div className="empty-state" style={{ padding: "32px 16px" }}>
              <div className="empty-icon" style={{ width: "48px", height: "48px" }}>
                <i className="fas fa-newspaper" style={{ fontSize: "1rem" }} aria-hidden="true" />
              </div>
              <div className="empty-title" style={{ fontSize: "0.9375rem" }}>
                {t("empty")}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsCard;
